import { Game } from "../../Game";
import { Intersects, LineToLineIntersect } from "../../Utils/2D Shapes/Intersects";
import { ScaleManager } from "../../Utils/ScaleManager";
import { Stadium } from "../Stadium/Stadium";
import { eGameplayState, Gameplay, TIMER } from "../Gameplay";
import { Ball, KickOffBend } from "./Ball";
import { SwipeDisplay } from "../HUD/SwipeDisplay";
import { HUD } from "../HUD/HUD";



/**
 * Nakresleni cesty vcetne krivky.
 * Vyska vykopnuti je urcena vertikalni pozici posledniho bodu swipu vuci dolnimu a hornimu okraji brany. Pokud swipe je prilis pomaly, tak vyska vykopu je minimalni.
 * Rychlost swipu je vzdy maximalni.
 */
export class ControlsC {
    /**
     * Maximalni doba swipu
     */
    private static readonly SWIPE_MAX_TIME_LEN = 2000;
    /**
     * Maximalni doba necinnosti swipu */
    private static readonly SWIPE_MAX_IDLE_LEN = 500;

    private _ball: Ball;

    /**
     * Paprsek pro konvezri 2D pozice na obrazovce na 3D pozici v hernim svetem
     */
    private _ray: pc.Ray = new pc.Ray();
    /**
     * AABB zeme pro prevod screen pozice na pozici na zemi ve 3D
     */
    private _groundAABB: pc.BoundingBox;

    private _swipePointerId: number;
    private _swipeStartTime: number;
    private _swipeStartPos: pc.Vec2 = new pc.Vec2();
    private _swipeEndPos: pc.Vec2 = new pc.Vec2();
    private _swipeMinLen: number;
    private _swipeDirectLen: number;
    private _swipeTotalLen: number;
    private _swipeIdleTime: number;

    // Zaznam bodu, pres ktere vede swipe
    private static readonly SP_ANGLE = 0;
    private static readonly SP_DISTANCE = 1;

    private _swipePoints = new Float32Array(1024 * 5);
    private _swipePointCnt: number;

    private _kickOffBend: KickOffBend;

    private _swipeStartWPos = new pc.Vec3();
    private _swipeEndWPos = new pc.Vec3();

    private _tmpSPos = new pc.Vec3();
    private _tmpWPos = new pc.Vec3();

    // Vykresleni swipu
    private _swipeDisplay: SwipeDisplay;
    private _swipeDisplayOpacityRatio: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(ball: Ball) {
        this._ball = ball;

        // AABB zeme pro prevod screen pozice na pozici na zemi ve 3D
        this._groundAABB = new pc.BoundingBox(new pc.Vec3(Stadium.CENTER_X, -Stadium.GROUND_HEIGHT / 2, 0), new pc.Vec3(Stadium.GROUND_WIDTH / 2, Stadium.GROUND_HEIGHT / 2, Stadium.LENGTH));

        // Zachytavat zmenu rozliseni
        Game.app.on(ScaleManager.EVENT_RESIZE, this.handleGameResChange, this);
        this.handleGameResChange();

        // Zakriveni
        this._kickOffBend = new KickOffBend();

        // Vykresleni swipu
        this._swipeDisplay = new SwipeDisplay();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        this._swipeStartTime = -1;
        this._swipeDisplay.reset();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (this._swipeStartTime >= 0) {
            let swipeRemTime = ControlsC.SWIPE_MAX_TIME_LEN - (TIMER.time - this._swipeStartTime);

            if (swipeRemTime < 500) {
                this._swipeDisplayOpacityRatio = Math.max(0, swipeRemTime / 500);
                this.setSwipeDisplayOpacity();

                if (this._swipeDisplayOpacityRatio == 0)
                    this.handlePointerUp();
            }

            if (this._swipeStartTime >= 0 && ControlsC.SWIPE_MAX_IDLE_LEN - (TIMER.time - this._swipeIdleTime) <= 0)
                this.handlePointerUp();
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    activate(): void {
        // Mys
        if (Game.app.mouse) {
            let mouse = Game.app.mouse;

            mouse.on(pc.EVENT_MOUSEDOWN, this.handleMouseDown, this);
            mouse.on(pc.EVENT_MOUSEMOVE, this.handleMouseMove, this);
            mouse.on(pc.EVENT_MOUSEUP, this.handleMouseUp, this);
        }

        // Touch
        if (Game.app.touch) {
            let touch = Game.app.touch;

            touch.on(pc.EVENT_TOUCHSTART, this.handleTouchStart, this);
            touch.on(pc.EVENT_TOUCHEND, this.handleTouchEnd, this);
            touch.on(pc.EVENT_TOUCHCANCEL, this.handleTouchEnd, this);
            touch.on(pc.EVENT_TOUCHMOVE, this.handleTouchMove, this);
        }

        // Hlidat ukonceni hry
        Gameplay.instance.events.on(Gameplay.EVENT_GAME_END, (): void => { this.reset(); });
    }

    //#region Mouse

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleMouseDown(event: pc.MouseEvent): void {
        if (event.button != pc.MOUSEBUTTON_LEFT)
            return;

        if (!this._ball.isKickOffReady) {
            if (Gameplay.instance.state == eGameplayState.intro)
                HUD.instance.manCityIntro.hide();

            return;
        }

        this.handlePointerDown(0, event.x, event.y);
        event.event.preventDefault();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleMouseMove(event: pc.MouseEvent): void {
        if (this._swipeStartTime < 0)
            return;

        if (Game.paused || Gameplay.instance.state != eGameplayState.play) {
            this._swipeStartTime = -1;
            return;
        }


        this.handlePointerMove(event.x, event.y);
        event.event.preventDefault();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleMouseUp(event: pc.MouseEvent): void {
        if (event.button != pc.MOUSEBUTTON_LEFT || this._swipeStartTime < 0)
            return;

        if (Game.paused || Gameplay.instance.state != eGameplayState.play) {
            this._swipeStartTime = -1;
            return;
        }

        this.handlePointerUp();
    }

    //#endregion

    //#region Touch

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleTouchStart(event: pc.TouchEvent): void {
        if (!this._ball.isKickOffReady) {
            if (Gameplay.instance.state == eGameplayState.intro)
                HUD.instance.manCityIntro.hide();

            return;
        }

        let touch = event.changedTouches[event.changedTouches.length - 1];

        this.handlePointerDown(touch.id, touch.x, touch.y);
        event.event.preventDefault();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleTouchEnd(event: pc.TouchEvent): void {
        if (this._swipeStartTime < 0)
            return;

        if (Game.paused || Gameplay.instance.state != eGameplayState.play) {
            this._swipeStartTime = -1;
            return;
        }

        let touches = event.changedTouches;
        let i = touches.length;

        while (i-- != 0) {
            if (touches[i].id == this._swipePointerId) {
                this.handlePointerUp();
                event.event.preventDefault();
                break;
            }
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleTouchMove(event: pc.TouchEvent): void {
        if (this._swipeStartTime < 0)
            return;

        if (Game.paused || Gameplay.instance.state != eGameplayState.play) {
            this._swipeStartTime = -1;
            return;
        }

        // Najit zaznam pro aktivni touch
        let touches = event.changedTouches;
        let i = touches.length;

        while (i-- != 0) {
            if (touches[i].id == this._swipePointerId) {
                this.handlePointerMove(touches[i].x, touches[i].y);
                break;
            }
        }

        event.event.preventDefault();
    }

    //#endregion

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handlePointerDown(pointerId: number, screenX: number, screenY: number): void {
        // Prvni pointDown odstartuje gameplay
        Gameplay.instance.start();

        // Ulozit pocatek swipu
        this._swipeStartTime = this._swipeIdleTime = TIMER.time;
        this._swipeStartPos.set(screenX, screenY);
        this._swipeEndPos.copy(this._swipeStartPos);
        this._swipePointerId = pointerId;
        this._swipeDirectLen = 0;
        this._swipeTotalLen = 0;

        // Zaznam bodu
        this._swipePointCnt = 0;

        // Vykresleni swipu
        let intToOutResRatio = Game.scale.internalToOuputResRatio;

        this._swipeDisplayOpacityRatio = 1;
        this.setSwipeDisplayOpacity();
        this._swipeDisplay.addPoint(screenX * intToOutResRatio, -screenY * intToOutResRatio, 0);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handlePointerMove(screenX: number, screenY: number): void {
        let intToOutResRatio = Game.scale.internalToOuputResRatio;

        // Vzdalenost od posledni pozice        
        let lastPosDis = Math.sqrt(Math.pow(this._swipeEndPos.x - screenX, 2) + Math.pow(this._swipeEndPos.y - screenY, 2));

        if (lastPosDis * intToOutResRatio >= 6)
            // Resetovat idle timer
            this._swipeIdleTime = TIMER.time;

        // Prima delka swipu (od start po posledni pozici tazeni)
        this._swipeDirectLen = Math.sqrt(Math.pow(this._swipeStartPos.x - screenX, 2) + Math.pow(this._swipeStartPos.y - screenY, 2));

        // Celkova delka swipu
        this._swipeTotalLen += lastPosDis;

        // Ulozit pozici
        this._swipeEndPos.set(screenX, screenY);

        // Pridat bod do seznamu bodu
        let i = this._swipePointCnt++ * 2;

        this._swipePoints[i + ControlsC.SP_ANGLE] = Math.atan2(screenY - this._swipeStartPos.y, screenX - this._swipeStartPos.x);    // Uhel
        this._swipePoints[i + ControlsC.SP_DISTANCE] = this._swipeDirectLen; // Vzdalenost

        // Nastavit barvu a opacity tecek podle delky swipu
        this.setSwipeDisplayOpacity();

        // Vykresleni swipu
        this._swipeDisplay.addPoint(screenX * intToOutResRatio, -screenY * intToOutResRatio, this._swipeTotalLen * intToOutResRatio);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handlePointerUp(): void {
        if (this._swipeDirectLen >= this._swipeMinLen) {
            // Vypocitat zakriveni
            this.calculateBend(Math.atan2(this._swipeEndPos.y - this._swipeStartPos.y, this._swipeEndPos.x - this._swipeStartPos.x));

            // Ziskat pozici leveho dolniho rohu brany na obrazovce
            let camera = Game.camera.camera;
            let wPos = this._tmpWPos.set(Stadium.CENTER_X - Stadium.GOAL_WIDTH / 2, 0, Stadium.GOAL_Z);
            let sPos = this._tmpSPos;

            camera.worldToScreen(wPos, sPos);

            let goalLBX = sPos.x;
            let goalLBY = sPos.y;

            // Ziskat pozici praveho dolniho rohu brany na obrazovce
            wPos.x = Stadium.GOAL_WIDTH;
            camera.worldToScreen(wPos, sPos);

            let goalRBX = sPos.x;
            let goalRBY = sPos.y;

            // Ziskat pozici praveho horniho rohu brany na obrazovce
            wPos.y += Stadium.GOAL_HEIGHT;
            camera.worldToScreen(wPos, sPos);

            let goalRTX = sPos.x;
            let goalRTY = sPos.y;

            // Ziskat pozici leveho horniho rohu brany na obrazovce
            wPos.x -= Stadium.GOAL_WIDTH;
            camera.worldToScreen(wPos, sPos);

            let goalLTX = sPos.x;
            let goalLTY = sPos.y;

            // Skrz konecnou pozici swipu na ose X vest kolmici a najit prusecik s prijmkou mezi spodnim levym a pravym okrajem brany
            let botIntersection = new LineToLineIntersect();
            let upPower = 0;

            Intersects.LineToLineInfinite(goalLBX, goalLBY, goalRBX, goalRBY, this._swipeEndPos.x, 0, this._swipeEndPos.x, 1000, botIntersection);

            if (this._swipeEndPos.y < botIntersection.y) {
                // Vypocitat v jake vysce vuci brane konci swipe

                // 1. skrz konecnou pozici swipu na ose X vest kolmici a najit prusecik s prijmkou mezi hornim levym a pravym okrajem brany
                let topIntersection = new LineToLineIntersect();

                Intersects.LineToLineInfinite(goalLTX, goalLTY, goalRTX, goalRTY, this._swipeEndPos.x, 0, this._swipeEndPos.x, 1000, topIntersection);

                // 2. Vypocitat pomer vzdalenosti konecne pozice swipu na ose Y od dolniho okraje brany vuci vzdalenosti horniho okraje brany od dolniho
                upPower = (botIntersection.y - this._swipeEndPos.y) / (botIntersection.y - topIntersection.y);

                // Upravit konecnou pozici swipu na Y, tak aby se smer pocital k dolnimu okraji brany
                this._swipeEndPos.y = botIntersection.y;
            }

            // Prevest pozici swipe start a swipe end na pozice v hernim svete
            camera.screenToWorld(this._swipeStartPos.x, this._swipeStartPos.y, camera.farClip, this._ray.direction);

            this._ray.origin.copy(Game.camera.getPosition());
            this._ray.direction.sub(this._ray.origin).normalize();
            this._groundAABB.intersectsRay(this._ray, this._swipeStartWPos);

            camera.screenToWorld(this._swipeEndPos.x, this._swipeEndPos.y, camera.farClip, this._ray.direction);

            this._ray.origin.copy(Game.camera.getPosition());
            this._ray.direction.sub(this._ray.origin).normalize();
            this._groundAABB.intersectsRay(this._ray, this._swipeEndWPos);

            this._ball.showMark(this._swipeEndWPos);

            // Pokud je startovni pozice swipe v hernim svete dostatecne blizko pozici mice, tak jako startovni pozici pouzit pozici mice
            if (this._swipeStartWPos.distance(this._ball.kickOffPos) <= Ball.RADIUS) {
                this._swipeStartWPos.copy(this._ball.kickOffPos);
                this._swipeStartWPos.y = 0;
            }

            // Vypocitat uhel vykopu
            let directAngle = Math.atan2(this._swipeEndWPos.z - this._swipeStartWPos.z, this._swipeEndWPos.x - this._swipeStartWPos.x);

            this._ball.kickOff(directAngle, 1, upPower, this._kickOffBend);
        }

        this.reset();
    }

    //-------------------------------------------------------------------------    
    //-------------------------------------------------------------------------
    private calculateBend(directAngle: number): void {
        this._kickOffBend.dir = 0;

        // Detekovat zakriveni
        let bufPos = 0;

        // Najit bod s nejvyssi vyskou od primky tazeni start <-> cil (vrchol zakriveni)
        let bendHeight = 0;
        let bendPointId: number;  // Index bodu s nejdelsi kolmici na prijmku start <-> cil

        for (let pointId = 0; pointId < this._swipePointCnt; pointId++) {
            // Vypocitat vzdalenost bodu od prijmky start <-> cil (SOH = sin(theta) = opposite / hypotenuse -> opposite = sin(theta) * hypotenuse)                
            let size = Math.sin(Math.abs(this._swipePoints[bufPos + ControlsC.SP_ANGLE] - directAngle)) * this._swipePoints[bufPos + ControlsC.SP_DISTANCE];

            // Ulozit bod s nejvetsi vzdalenosti (vyskou)
            if (size > bendHeight) {
                bendHeight = size;
                bendPointId = pointId;
            }

            bufPos += 2;
        }

        // Ziskat pomer vysky vrcholu vuci prime delce swipu
        bendHeight /= this._swipeDirectLen;

        if (bendHeight < 0.1)
            return;

        this._kickOffBend.vertexHeight = bendHeight;

        // Je zakriveni smerem vlevo nebo vpravo od primky start <-> cil?
        bufPos = bendPointId * 2;

        let angle = this._swipePoints[bufPos + ControlsC.SP_ANGLE] - directAngle;

        this._kickOffBend.dir = angle < 0 ? -1 : 1;

        // Vypocitat offset vrcholu zakriveni na prijmce start <-> cil od pozice start (CAH = cos(theta) = adjacent / hypotenuse -> adjacent = cos(theta) * hypotenuse)
        this._kickOffBend.vertexOffset = Math.cos(Math.abs(angle)) * this._swipePoints[bufPos + ControlsC.SP_DISTANCE];
        this._kickOffBend.vertexOffset /= this._swipeDirectLen; // Prevest na pomer 0 az 1
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private setSwipeDisplayOpacity(): void {
        // Vypocitat opacity podle prime delky swipu
        this._swipeDisplay.opacity = (this._swipeDirectLen >= this._swipeMinLen ? 0.75 : 0.25) * this._swipeDisplayOpacityRatio;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleGameResChange(): void {
        this._swipeMinLen = Game.scale.outputWidth / 3;
    }
}